/* /banking-docs · Internal viewer for the sponsor-bank diligence package.
   Lists all docs, renders them inline as markdown, and exposes download buttons.
   Not linked from public nav — accessed by direct URL.
*/
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Download, FileText, ChevronRight, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/banking-docs`;

const DOC_TITLES = {
  "README.md": "📦 Package README · Start here",
  "01_flow_of_funds.md": "01 · Flow of Funds",
  "02_evidence_of_activity.md": "02 · Evidence of Activity",
  "03_aml_bsa_compliance_program.md": "03 · AML / BSA Compliance Program",
  "04_kyb_kyc_procedures.md": "04 · KYB / KYC Procedures",
  "05_mtl_sponsor_bank_structure.md": "05 · MTL / Sponsor-Bank Structure",
  "06_volume_projections.md": "06 · Volume Projections",
};

const DOC_STATUS = {
  "README.md": { label: "Index", tone: "slate" },
  "01_flow_of_funds.md": { label: "Ready", tone: "lime" },
  "02_evidence_of_activity.md": { label: "Ready", tone: "lime" },
  "03_aml_bsa_compliance_program.md": { label: "Template — needs BSA Officer + counsel", tone: "amber" },
  "04_kyb_kyc_procedures.md": { label: "Ready", tone: "lime" },
  "05_mtl_sponsor_bank_structure.md": { label: "Template — needs counsel", tone: "amber" },
  "06_volume_projections.md": { label: "Template — needs your GTM numbers", tone: "amber" },
};

const TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  lime:  "bg-lime-50 text-lime-800 border-lime-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
};

export default function BankingDocs() {
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState(null);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "verqo · Bank diligence docs";
    fetch(`${API}/list`)
      .then(r => r.json())
      .then(d => {
        const list = d.files || [];
        // Sort with README first, then numeric
        list.sort((a, b) => {
          if (a.filename === "README.md") return -1;
          if (b.filename === "README.md") return 1;
          return a.filename.localeCompare(b.filename);
        });
        setFiles(list);
        if (list.length > 0) selectFile(list[0].filename);
      })
      .catch(e => console.error("list failed", e));
  }, []);

  const selectFile = (filename) => {
    setActive(filename);
    setLoading(true);
    fetch(`${API}/file/${filename}`)
      .then(r => r.text())
      .then(t => { setBody(t); setLoading(false); })
      .catch(e => { setBody(`# Error\n\n${e.message}`); setLoading(false); });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900" data-testid="banking-docs-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[#0c1612] grid place-items-center">
              <FileText className="w-4 h-4 text-lime-300" />
            </div>
            <div>
              <div className="text-[14px] font-semibold tracking-tight">Verqo · Bank Diligence Package</div>
              <div className="text-[11px] text-slate-500">Internal preview · do not share until reviewed by counsel</div>
            </div>
          </div>
          <a
            href={`${API}/download-all`}
            data-testid="download-all-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0c1612] text-white text-[12.5px] font-semibold hover:opacity-90 transition-opacity"
          >
            <Download className="w-3.5 h-3.5" />
            Download all (ZIP)
          </a>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-8">
        {/* Sidebar — file list */}
        <aside className="md:sticky md:top-24 md:self-start" data-testid="docs-sidebar">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">
            Documents · {files.length}
          </div>
          <ul className="space-y-1.5">
            {files.map(f => {
              const title  = DOC_TITLES[f.filename] || f.filename;
              const status = DOC_STATUS[f.filename] || { label: "", tone: "slate" };
              const isActive = active === f.filename;
              return (
                <li key={f.filename}>
                  <button
                    type="button"
                    onClick={() => selectFile(f.filename)}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 transition-all group ${
                      isActive
                        ? "border-lime-400 bg-lime-50"
                        : "border-slate-200 bg-white hover:border-lime-300"
                    }`}
                    data-testid={`doc-tab-${f.filename}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-900 leading-snug truncate">
                          {title}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-wider font-semibold border ${TONE_CLASSES[status.tone]}`}>
                            {status.label}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className={`w-3.5 h-3.5 mt-1 flex-shrink-0 transition-transform ${isActive ? "rotate-90 text-lime-700" : "text-slate-400"}`} />
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-[10px] text-slate-500">
                      <span>{(f.size_bytes / 1024).toFixed(1)} KB</span>
                      <a
                        href={`${API}/download/${f.filename}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors"
                        data-testid={`doc-download-${f.filename}`}
                      >
                        <Download className="w-2.5 h-2.5" /> .md
                      </a>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-[11px] text-amber-900 leading-relaxed" data-testid="readiness-warning">
            <div className="font-semibold mb-1">Before sending to a bank:</div>
            <ul className="space-y-1 list-disc list-inside marker:text-amber-600">
              <li>Designate a BSA Officer</li>
              <li>Get fintech-counsel review of Docs 03 + 05</li>
              <li>Fill Doc 06 with real GTM numbers</li>
              <li>Rotate admin password from <code className="bg-amber-100 px-1 rounded">admin123</code></li>
            </ul>
          </div>
        </aside>

        {/* Document body */}
        <main className="bg-white rounded-2xl border border-slate-200 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.18)]" data-testid="doc-body">
          <div className="px-6 md:px-10 py-5 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[12px] text-slate-500 font-mono">{active || "—"}</div>
            {active && (
              <a
                href={`${API}/download/${active}`}
                className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="active-doc-download"
              >
                <Download className="w-3 h-3" /> Download this doc
              </a>
            )}
          </div>
          <article className="px-6 md:px-10 py-8 prose prose-slate max-w-none prose-headings:tracking-tight prose-h1:text-3xl prose-h1:font-semibold prose-h1:mt-0 prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-base prose-h3:font-semibold prose-h3:mt-6 prose-p:text-[14px] prose-p:leading-relaxed prose-li:text-[14px] prose-li:leading-relaxed prose-table:text-[13px] prose-th:bg-slate-50 prose-th:font-semibold prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border-t prose-td:border-slate-100 prose-code:text-[12.5px] prose-code:bg-slate-100 prose-code:px-1 prose-code:rounded prose-code:font-normal prose-code:before:hidden prose-code:after:hidden prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:text-[12px] prose-pre:rounded-lg prose-a:text-lime-700 prose-a:no-underline hover:prose-a:underline">
            {loading
              ? <div className="text-slate-400 text-[13px]">Loading…</div>
              : <ReactMarkdown>{body}</ReactMarkdown>
            }
          </article>
        </main>
      </div>

      <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 text-[11px] text-slate-500 flex items-center justify-between">
        <span>verqo · sponsor-bank diligence package</span>
        <a
          href={`${API}/download-all`}
          className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors"
        >
          <Download className="w-3 h-3" /> Download package (.zip)
        </a>
      </footer>
    </div>
  );
}
